import { useMsal } from '@azure/msal-react'
import { loginRequest } from '../auth/authConfig'
import { Hammer, FileText, BarChart3, Grid3X3, MessageSquare } from 'lucide-react'

export default function Login() {
  const { instance } = useMsal()

  const handleLogin = () => {
    instance.loginRedirect(loginRequest)
  }

  return (
    <div className="min-h-screen flex bg-gradient-dark">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20" />
        <div className="absolute top-1/4 -left-20 w-80 h-80 bg-blue-500/30 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl" />

        <div className="relative z-10 p-12 flex flex-col justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                <Hammer className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold gradient-text">SprintForge</span>
            </div>
          </div>

          <div className="space-y-8">
            <h1 className="text-4xl font-bold text-white leading-tight">
              AI-Powered Project
              <br />
              <span className="gradient-text">Management</span>
            </h1>
            <p className="text-xl text-slate-300">
              Transform your WBS and SOW documents into actionable Scrum artifacts with intelligent
              gap analysis and Smartsheets integration.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-panel p-4">
                <FileText className="w-6 h-6 text-blue-400 mb-2" />
                <h3 className="font-medium text-slate-200">Document Ingestion</h3>
                <p className="text-sm text-slate-400 mt-1">Upload WBS, SOW, transcripts</p>
              </div>
              <div className="glass-panel p-4">
                <MessageSquare className="w-6 h-6 text-cyan-400 mb-2" />
                <h3 className="font-medium text-slate-200">AI Chat</h3>
                <p className="text-sm text-slate-400 mt-1">Ask questions with citations</p>
              </div>
              <div className="glass-panel p-4">
                <BarChart3 className="w-6 h-6 text-purple-400 mb-2" />
                <h3 className="font-medium text-slate-200">Gap Analysis</h3>
                <p className="text-sm text-slate-400 mt-1">AI-powered insights</p>
              </div>
              <div className="glass-panel p-4">
                <Grid3X3 className="w-6 h-6 text-green-400 mb-2" />
                <h3 className="font-medium text-slate-200">Smartsheets</h3>
                <p className="text-sm text-slate-400 mt-1">Direct integration</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span>Powered by</span>
            <span className="text-slate-400">Azure OpenAI</span>
            <span>&</span>
            <span className="text-slate-400">Smartsheets</span>
          </div>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                <Hammer className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold gradient-text">SprintForge</span>
            </div>
            <p className="text-slate-400">AI-Powered Project Management</p>
          </div>

          <div className="glass-panel p-8">
            <h2 className="text-2xl font-bold text-slate-100 mb-2">Welcome back</h2>
            <p className="text-slate-400 mb-8">Sign in with your Microsoft account to continue</p>

            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center px-6 py-3.5 bg-white text-slate-900 font-medium rounded-lg hover:bg-slate-100 transition-colors shadow-lg"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 21 21" fill="none">
                <rect width="10" height="10" fill="#f25022" />
                <rect x="11" width="10" height="10" fill="#7fba00" />
                <rect y="11" width="10" height="10" fill="#00a4ef" />
                <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </button>

            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 h-px bg-slate-700" />
              <span className="text-xs text-slate-500">Secured by Microsoft Entra ID</span>
              <div className="flex-1 h-px bg-slate-700" />
            </div>

            <p className="mt-6 text-center text-sm text-slate-500">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
